// Project Board tab (Screen 3) — kanban + native HTML5 DnD + toast + context rail.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, ButtonBase, CircularProgress } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../../design/brainTheme";
import { AddWorkBar, EmptyState, Icon } from "../../../mui";
import { useProfile } from "../../../lib/profile";
import {
  getProjectJiraIssues,
  type JiraScope,
  type NamedScope,
  type ProjectJiraIssuesResponse,
} from "../../../lib/brain-api";
import { BoardCard } from "./BoardCard";
import { IssueDrawer } from "./IssueDrawer";
import { AdminStrips } from "./AdminStrips";
import { ContextRail } from "./ContextRail";
import {
  useBoardDnd,
  type BoardColumnKey,
  type BoardIssue,
  type BoardToast,
  type FailedDrop,
} from "./useBoardDnd";

const c = brainTokens.color;

/** Jira project key for create — mirrors server default-board derivation. */
function defaultBoardFromScope(scope: JiraScope | null): string | undefined {
  if (!scope) return undefined;
  if (scope.type === "epic") return scope.key.split("-")[0] || undefined;
  if (scope.type === "fixVersion") return scope.project;
  if (scope.type === "project") return scope.key;
  return undefined;
}

const COLUMNS: { key: BoardColumnKey; label: string }[] = [
  { key: "new", label: "TO DO" },
  { key: "indeterminate", label: "IN PROGRESS" },
  { key: "done", label: "DONE" },
];

/** Tinted header band per column, per the redesign spec. */
function columnHeaderTint(key: BoardColumnKey): { bg: string; fg: string } {
  if (key === "indeterminate") return { bg: c.tealTint2, fg: c.tealInk };
  if (key === "done") return { bg: c.successBg, fg: c.successText };
  return { bg: c.panelBg2, fg: c.ink };
}

/** 3px status spine on each card: planning (in flight) teal, backlog placeholder, done success. */
function cardSpine(col: BoardColumnKey, backlog: boolean): string {
  if (col === "done") return c.successDot;
  if (backlog) return c.placeholder;
  return c.teal;
}

function scopeChip(scope: JiraScope): string {
  if (scope.type === "epic") return scope.key;
  if (scope.type === "fixVersion") return `${scope.project} ${scope.version}`;
  if (scope.type === "project") return scope.key;
  return scope.label;
}

function formatScopes(
  scopes: NamedScope[],
  fallback: JiraScope | null
): { primary: string; extras: string[] } {
  if (scopes.length > 0) {
    const chips = scopes.map((s) => s.name || scopeChip(s.scope));
    return { primary: chips[0], extras: chips.slice(1) };
  }
  if (fallback) return { primary: scopeChip(fallback), extras: [] };
  return { primary: "", extras: [] };
}

function syncedLabel(at: number | null): string {
  if (!at) return "synced —";
  const mins = Math.max(0, Math.round((Date.now() - at) / 60000));
  if (mins < 1) return "synced just now";
  return `synced ${mins}m ago`;
}

function isBacklogStatus(status: string): boolean {
  return /^backlog$/i.test(status.trim());
}

/** Rank proxy: non-Backlog first (by updated DESC), Backlog sunk at tail. */
function sortTodo(rows: BoardIssue[]): BoardIssue[] {
  const active = rows
    .filter((i) => !isBacklogStatus(i.status))
    .sort((a, b) => Date.parse(b.updated) - Date.parse(a.updated));
  const backlog = rows
    .filter((i) => isBacklogStatus(i.status))
    .sort((a, b) => Date.parse(b.updated) - Date.parse(a.updated));
  return [...active, ...backlog];
}

function sortByUpdated(rows: BoardIssue[]): BoardIssue[] {
  return [...rows].sort((a, b) => Date.parse(b.updated) - Date.parse(a.updated));
}

const DONE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

/** Done column: only issues updated in the last 14 days, show every issue in that window. */
function recentDone(rows: BoardIssue[]): { rows: BoardIssue[]; count: number } {
  const cutoff = Date.now() - DONE_WINDOW_MS;
  const inWindow = sortByUpdated(rows.filter((i) => Date.parse(i.updated) >= cutoff));
  return { rows: inWindow, count: inWindow.length };
}

export function ProjectBoardTab({
  projectId,
  isAdmin = false,
}: {
  projectId: string;
  isAdmin?: boolean;
}) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { profile } = useProfile();
  const [searchParams] = useSearchParams();
  const mineRequested = searchParams.get("mine") === "1";
  const loadSeq = useRef(0);
  const myAccountId = profile?.jiraAccountId;
  const myName = profile?.displayName?.trim() ?? "";

  const [data, setData] = useState<ProjectJiraIssuesResponse | null>(null);
  const [issues, setIssues] = useState<BoardIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncedAt, setSyncedAt] = useState<number | null>(null);
  const [mine, setMine] = useState(mineRequested);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [railKey, setRailKey] = useState(0);
  const [, setTick] = useState(0);

  const retryRef = useRef<(f: FailedDrop) => void>(() => {});

  const onToast = useCallback(
    (opts: BoardToast) => {
      const bad = opts.tone === "bad";
      enqueueSnackbar(opts.text, {
        variant: bad ? "error" : "default",
        autoHideDuration: bad ? 6000 : 3200,
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        action: opts.retry
          ? (id) => (
              <ButtonBase
                onClick={() => {
                  closeSnackbar(id);
                  retryRef.current(opts.retry!);
                }}
                sx={{
                  color: c.tealInk,
                  fontSize: 12.5,
                  fontWeight: 600,
                  px: 1,
                  fontFamily: brainTokens.font.sans,
                }}
              >
                Retry
              </ButtonBase>
            )
          : undefined,
        style: bad
          ? {
              background: c.dangerBg,
              color: c.dangerText,
              border: `1px solid ${c.dangerBorder}`,
            }
          : {
              background: c.pageBg,
              color: c.ink2,
              border: `1px solid ${c.border}`,
            },
      });
    },
    [closeSnackbar, enqueueSnackbar]
  );

  const dnd = useBoardDnd({
    projectId,
    issues,
    setIssues,
    onToast,
  });
  retryRef.current = dnd.retryDrop;

  const reload = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const r = await getProjectJiraIssues(projectId);
      if (seq !== loadSeq.current) return;
      setData(r);
      setIssues(r.issues.map((i) => ({ ...i })));
      setSyncedAt(Date.now());
    } catch (e) {
      if (seq !== loadSeq.current) return;
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to load Jira issues", {
        variant: "error",
      });
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [enqueueSnackbar, projectId]);

  useEffect(() => {
    setData(null);setIssues([]);setOpenKey(null);
    void reload();
    return () => {loadSeq.current++;};
  }, [reload]);
  useEffect(() => {setMine(mineRequested);},[projectId,mineRequested]);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    if (!mine) return issues;
    return issues.filter((i) => myAccountId
      ? i.assigneeAccountId === myAccountId
      : Boolean(myName) && (i.assignee ?? "").toLowerCase() === myName.toLowerCase());
  }, [issues, mine, myName, myAccountId]);

  const byCol = useMemo(() => {
    const map: Record<BoardColumnKey, BoardIssue[]> = {
      new: [],
      indeterminate: [],
      done: [],
    };
    for (const i of filtered) {
      const cat = String(i.statusCategory ?? "").toLowerCase();
      if (cat === "done") map.done.push(i);
      else if (cat === "indeterminate") map.indeterminate.push(i);
      else map.new.push(i);
    }
    const done = recentDone(map.done);
    return {
      new: sortTodo(map.new),
      indeterminate: sortByUpdated(map.indeterminate),
      done: done.rows,
      doneCount: done.count,
    };
  }, [filtered]);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={22} sx={{ color: c.teal }} />
      </Box>
    );
  }

  const hasScope = Boolean(data?.scope || data?.scopes?.length);
  const { primary, extras } = formatScopes(data?.scopes ?? [], data?.scope ?? null);
  const zeroIssues = issues.length === 0;
  const defaultBoard =
    defaultBoardFromScope(data?.scope ?? null) ??
    defaultBoardFromScope(data?.scopes?.[0]?.scope ?? null);

  return (
    <Box sx={{ pt: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
      {data?.truncated && <Alert severity="warning">This project's Jira scopes contain more issues than the loaded board. Counts are partial. Today shows your assigned work across all pages.</Alert>}
      <Box>
        {hasScope && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              background: c.white,
              border: `1px solid ${c.border}`,
              borderRadius: `${brainTokens.radius.card}px`,
              padding: "12px 18px",
            }}
          >
            <Box
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 10,
                letterSpacing: "0.1em",
                color: c.faint,
              }}
            >
              SCOPE
            </Box>
            {primary && (
              <Box
                sx={{
                  fontFamily: brainTokens.font.mono,
                  fontSize: 11.5,
                  color: c.tealInk,
                  background: c.tealTint,
                  border: `1px solid ${c.tealTintBorder}`,
                  borderRadius: `${brainTokens.radius.pill}px`,
                  px: "12px",
                  py: "4px",
                  whiteSpace: "nowrap",
                  flex: "none",
                }}
              >
                {primary}
              </Box>
            )}
            {extras.map((ex) => (
              <Box
                key={ex}
                sx={{
                  fontFamily: brainTokens.font.mono,
                  fontSize: 11.5,
                  color: c.muted2,
                  whiteSpace: "nowrap",
                }}
              >
                + {ex}
              </Box>
            ))}
            <Box sx={{ fontSize: 12.5, color: c.muted2 }}>{syncedLabel(syncedAt)}</Box>
            <Box sx={{ flex: 1 }} />
            <ButtonBase
              onClick={() => void reload()}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: 12.5,
                color: c.muted,
                fontFamily: brainTokens.font.sans,
                py: 0.5,
                px: 0.75,
                borderRadius: `${brainTokens.radius.chip}px`,
                "&:hover": { color: c.tealInk, background: c.tealTint2 },
              }}
            >
              <Icon name="refresh" size={15} />
              Refresh
            </ButtonBase>
            <ButtonBase
              onClick={() => setMine((v) => !v)}
              sx={{
                fontSize: 12.5,
                px: "12px",
                py: "6px",
                borderRadius: `${brainTokens.radius.button}px`,
                border: `1px solid ${mine ? c.teal : c.border}`,
                background: mine ? c.tealTint : c.white,
                color: mine ? c.tealInk : c.ink3,
                fontFamily: brainTokens.font.sans,
                fontWeight: 500,
              }}
            >
              Mine
            </ButtonBase>
          </Box>
        )}

        <Box sx={{ mt: hasScope ? "12px" : 0 }}>
          <AddWorkBar
            scope="board"
            projectId={projectId}
            defaultBoard={defaultBoard}
            types={hasScope ? undefined : ["note", "question"]}
            onCreated={(r) => {
              if (r.kind === "jira") void reload();
              else setRailKey((n) => n + 1);
            }}
          />
        </Box>
      </Box>

      {!hasScope ? (
        <EmptyState icon="folder-plus" title="No Jira scope linked yet">
          Pick the label, epic or fixVersion this project claims and the board fills itself. Notes and
          questions work without Jira — the add bar keeps those two types live.
        </EmptyState>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: "14px",
              alignItems: "start",
            }}
          >
            {COLUMNS.map((col) => {
              const rows = byCol[col.key];
              const over = dnd.dragOver === col.key;
              const count = col.key === "done" ? byCol.doneCount : rows.length;
              const tint = columnHeaderTint(col.key);

              return (
                <Box
                  key={col.key}
                  onDragOver={(e) => dnd.onDragOverCol(col.key, e)}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      dnd.onDragLeaveCol();
                    }
                  }}
                  onDrop={(e) => dnd.onDropCol(col.key, e)}
                  sx={{
                    background: c.white,
                    border: `1px solid ${over ? c.teal : c.border}`,
                    borderRadius: `${brainTokens.radius.card}px`,
                    overflow: "hidden",
                    minHeight: 220,
                    transition: "border-color 140ms ease",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "11px 16px",
                      background: tint.bg,
                      borderBottom: `1px solid ${c.borderLight}`,
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: brainTokens.font.mono,
                        fontSize: 10,
                        letterSpacing: "0.12em",
                        color: tint.fg,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.label}
                    </Box>
                    <Box
                      sx={{
                        fontFamily: brainTokens.font.mono,
                        fontSize: 10.5,
                        color: tint.fg,
                        background: c.white,
                        border: `1px solid ${c.borderLight}`,
                        borderRadius: `${brainTokens.radius.pill}px`,
                        px: "6px",
                      }}
                    >
                      {count}
                    </Box>
                    {col.key === "done" && (
                      <>
                        <Box sx={{ flex: 1 }} />
                        <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, color: tint.fg }}>14 days</Box>
                      </>
                    )}
                  </Box>

                  <Box sx={{ p: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    {rows.map((issue) => {
                      const backlog = col.key === "new" && isBacklogStatus(issue.status);
                      return (
                        <BoardCard
                          key={issue.key}
                          issue={issue}
                          quiet={col.key === "done"}
                          sunk={backlog}
                          spine={cardSpine(col.key, backlog)}
                          lifting={dnd.dragKey === issue.key}
                          settling={dnd.settling === issue.key}
                          ambiguousOptions={dnd.pending?.key === issue.key ? dnd.pending.options : null}
                          onPickTransition={dnd.pickTransition}
                          onOpen={() => setOpenKey(issue.key)}
                          onDragStart={() => dnd.onDragStart(issue.key)}
                          onDragEnd={dnd.onDragEnd}
                        />
                      );
                    })}

                    {col.key === "new" && rows.length === 0 && (
                      <Box sx={{ fontSize: 12.5, color: c.faint, lineHeight: 1.55 }}>
                        {zeroIssues ? "Nothing to do." : mine ? "Nothing assigned to you." : "Nothing to do."}
                      </Box>
                    )}
                    {col.key === "indeterminate" && rows.length === 0 && (
                      <Box sx={{ fontSize: 12.5, color: c.faint, lineHeight: 1.55 }}>
                        Nothing in flight. Drag a card here to start it — it transitions in Jira.
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}

            <ContextRail projectId={projectId} refreshKey={railKey} />
          </Box>

          <AdminStrips projectId={projectId} isAdmin={isAdmin} onChanged={() => void reload()} />

          <IssueDrawer
            projectId={projectId}
            issueKey={openKey}
            boardName={primary || undefined}
            onClose={() => setOpenKey(null)}
            onChanged={() => void reload()}
          />
        </>
      )}
    </Box>
  );
}
